import {
  DISPATCHED_NOTIFICATION_CHANNELS,
  NOTIFICATION_SENDER_PROVIDERS,
  SENDER_CHANNELS,
} from '@btsis/core';
import { oc } from '@orpc/contract';
import { z } from 'zod';
import { commonErrors } from './common/errors.js';

const guarded = {
  UNAUTHENTICATED: commonErrors.UNAUTHENTICATED,
  PERMISSION_DENIED: commonErrors.PERMISSION_DENIED,
  OUT_OF_SCOPE: commonErrors.OUT_OF_SCOPE,
  NOT_FOUND: commonErrors.NOT_FOUND,
  CONFLICT: commonErrors.CONFLICT,
  VALIDATION_FAILED: commonErrors.VALIDATION_FAILED,
};

export const notificationSenderProviderSchema = z.enum(NOTIFICATION_SENDER_PROVIDERS);
export type NotificationSenderProviderName = z.infer<typeof notificationSenderProviderSchema>;

export const senderChannelSchema = z.enum(SENDER_CHANNELS);

/**
 * **Everything a route ever says about an organization's sender, and no field
 * of it is secret.** There is no reveal route and no schema here that could
 * carry sealed material — a sender's key is replaced, never displayed
 * (docs/decisions/0053 §1, `.claude/rules/secrets.md`).
 *
 * `publicRef` is exactly the provider's own non-secret identifiers — a Meta
 * phone number id, a Netgsm alphanumeric header, the address of a gateway
 * session — which the provider itself treats as addressing rather than as a
 * credential. A secret never goes in it, which
 * `organization_notification_senders_public_ref_matches_provider` enforces
 * under the service by naming the keys each provider may carry.
 */
export const notificationSenderStateSchema = z.object({
  provider: notificationSenderProviderSchema,
  channels: z.array(senderChannelSchema),
  configured: z.boolean(),
  isActive: z.boolean(),
  fingerprint: z.string().nullable(),
  keyId: z.string().nullable(),
  onCurrentKey: z.boolean(),
  publicRef: z.record(z.string(), z.string()),
  configuredAt: z.string().nullable(),
  configuredByName: z.string().nullable(),
});

export type NotificationSenderState = z.infer<typeof notificationSenderStateSchema>;

/**
 * What one channel will actually do for this organization, which is the whole
 * point of the screen: its own sender, the deployment's shared account, or
 * nothing at all — in which case that channel does not send and every message
 * on it lands `failed` with `NOTIFICATION_PROVIDER_UNCONFIGURED`.
 */
export const notificationChannelPlanSchema = z.object({
  channel: z.enum(DISPATCHED_NOTIFICATION_CHANNELS),
  source: z.enum(['organization', 'shared', 'none']),
  provider: z.string().nullable(),
});

export type NotificationChannelPlan = z.infer<typeof notificationChannelPlanSchema>;

const secret = z.string().trim().min(8).max(512);
const reference = z.string().trim().min(2).max(200);
const baseUrl = z.url().max(300);

/** Which fields belong to which provider — the contract's copy of the catalogue. */
const FIELDS = {
  netgsm: ['userCode', 'msgHeader', 'apiBaseUrl', 'password'],
  whatsapp_business: ['phoneNumberId', 'apiBaseUrl', 'apiVersion', 'languageCode', 'accessToken'],
} as const;

const EVERY_FIELD = [
  'userCode',
  'msgHeader',
  'apiBaseUrl',
  'password',
  'phoneNumberId',
  'apiVersion',
  'languageCode',
  'accessToken',
] as const;

/**
 * Flat on purpose, for the reason 0053's payment input is: a secret nested one
 * level deeper than `body.<field>` would fall outside `REDACTED_LOG_PATHS`'
 * single wildcard and be logged.
 */
const setInputSchema = z
  .object({
    organizationId: z.uuid(),
    provider: notificationSenderProviderSchema,
    userCode: reference.optional(),
    msgHeader: reference.optional(),
    apiBaseUrl: baseUrl.optional(),
    password: secret.optional(),
    phoneNumberId: reference.optional(),
    apiVersion: reference.optional(),
    languageCode: reference.optional(),
    accessToken: secret.optional(),
  })
  .superRefine((input, context) => {
    const required: readonly string[] = FIELDS[input.provider];
    const given = input as unknown as Record<string, string | undefined>;
    for (const field of required) {
      if (given[field] === undefined) {
        context.addIssue({ code: 'custom', path: [field], message: 'required' });
      }
    }
    for (const field of EVERY_FIELD) {
      if (!required.includes(field) && given[field] !== undefined) {
        context.addIssue({
          code: 'custom',
          path: [field],
          message: 'not a field of this provider',
        });
      }
    }
  });

/**
 * An organization's own messaging senders. **Reads** are organization scope
 * behind `organization.view`, as every other organization read is; **writes**
 * are `organization.edit` at global scope alone — the gate the organization's
 * own rows already have, because which account an organization sends from is
 * the same kind of decision as which account its money arrives in
 * (docs/decisions/0053 §1, `.claude/rules/rbac-scope.md`).
 */
export const notificationSenderContract = {
  list: oc
    .route({
      method: 'GET',
      path: '/organizations/{organizationId}/notification-senders',
      summary:
        'Whether each provider is configured for this organization, and what each channel will do — never the credentials',
    })
    .errors(guarded)
    .input(z.object({ organizationId: z.uuid() }))
    .output(
      z.object({
        senders: z.array(notificationSenderStateSchema),
        plan: z.array(notificationChannelPlanSchema),
        writable: z.boolean(),
        /** False where no key ring is in the environment: a write would have nothing to seal under. */
        sealingAvailable: z.boolean(),
      }),
    ),

  set: oc
    .route({
      method: 'PUT',
      path: '/organizations/{organizationId}/notification-senders/{provider}',
      summary: 'Set or replace one provider’s sender. A credential is replaced, never displayed',
    })
    .errors(guarded)
    .input(setInputSchema)
    .output(z.object({ sender: notificationSenderStateSchema })),

  clear: oc
    .route({
      method: 'POST',
      path: '/organizations/{organizationId}/notification-senders/{provider}/clear',
      summary:
        'Stop sending on this organization’s own sender, keeping the sealed row for the trail',
    })
    .errors(guarded)
    .input(z.object({ organizationId: z.uuid(), provider: notificationSenderProviderSchema }))
    .output(z.object({ sender: notificationSenderStateSchema })),
};
